import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import {
    Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogContent,
    FormControlLabel, IconButton, Menu, MenuItem, Paper, Portal, Stack,
    TextField, Tooltip, Typography
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ActionButton from "../../../component/common/ActionButton";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import BallotRoundedIcon from "@mui/icons-material/BallotRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import ViewModuleRoundedIcon from "@mui/icons-material/ViewModuleRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import DOMPurify from "dompurify";
import { channelApi } from "../api/channelApi";
import { api } from "../../../util/api";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import dayjs from "dayjs";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";
import RichEditor from "../../../component/common/RichTextEditor";
import FeedComposeModal from "./FeedComposeModal";
import AttachmentPreviewModal from "../../mail/components/AttachmentPreviewModal";
import { extractMentionedEmployeeCodes, withoutSelf } from "../utils/mentions";
import { getApiErrorMessage } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";
import "dayjs/locale/ko";
import relativeTime from "dayjs/plugin/relativeTime";
import { formatFileSize } from "../../../util/numberFormat";

dayjs.locale("ko");
dayjs.extend(relativeTime);

const BASE = process.env.REACT_APP_API_REAL_URL;
const API_BASE = process.env.REACT_APP_API_BASE_URL;

const C = ds.colors;

/**
 * 피드 카드 한 장의 면.
 *
 * <p>{@code borderLight} 를 쓴다(일반 {@code border} 보다 한 단계 옅음) — 카드가 여러 장 쌓이면
 * 테두리도 그만큼 쌓여, 진한 선이면 화면이 격자처럼 무거워진다.
 */
const FEED_CARD_SX = {
    bgcolor: C.surface,
    border: `1px solid ${C.borderLight}`,
    borderRadius: 1.5,
    overflow: "hidden",
};

function formatTime(dt) {
    if (!dt) return "";
    return dayjs(dt).format("YYYY-MM-DD HH:mm");
}

// 7일 이내 글은 "3시간 전"처럼 상대 시간, 그 이전은 날짜로 표시한다.
function formatRelativeTime(dt) {
    if (!dt) return "";
    const d = dayjs(dt);
    if (dayjs().diff(d, "day") < 7) return d.fromNow();
    return d.format("YYYY.MM.DD");
}

function stripHtml(html) {
    if (!html) return "";
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

// readFileAsDataUrl, FontSize, FONT_FAMILIES, FONT_SIZES, TEXT_COLORS, RichEditor:
// component/common/RichTextEditor.jsx 로 이전. import 로 사용.

function fileIcon(contentType) {
    if (!contentType) return <InsertDriveFileRoundedIcon />;
    if (contentType.includes("pdf")) return <PictureAsPdfRoundedIcon sx={{ color: "#ef4444" }} />;
    if (contentType.startsWith("image/")) return <ImageRoundedIcon sx={{ color: "#3b82f6" }} />;
    return <InsertDriveFileRoundedIcon sx={{ color: "#6b7280" }} />;
}


/** 게시글 좋아요 버튼 (하트 + 개수). 클릭 버블 차단. */
function LikeButton({ feed, onToggle, size = 16 }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.4}
            onClick={(e) => { e.stopPropagation(); onToggle?.(feed.id); }}
            sx={{ cursor: "pointer", userSelect: "none", color: feed.likedByMe ? C.error : C.textMuted, "&:hover": { color: C.error } }}>
            {feed.likedByMe ? <FavoriteRoundedIcon sx={{ fontSize: size }} /> : <FavoriteBorderRoundedIcon sx={{ fontSize: size }} />}
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "inherit" }}>{feed.likeCount ?? 0}</Typography>
        </Stack>
    );
}

function PendingFileChip({ file, onRemove }) {
    return (
        <Chip
            icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 13 }} />}
            label={file.name}
            size="small"
            onDelete={onRemove}
            sx={{ maxWidth: 200, fontSize: 12, bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}
        />
    );
}

/** 채널 첨부(비이미지) 미리보기 가능 여부 — PDF·엑셀·텍스트·오피스(서버 PDF 변환). */
function channelPreviewable(att) {
    const m = att.contentType || "";
    const name = att.originalFileName || "";
    return m.includes("pdf") || m.startsWith("text/")
        || m.includes("spreadsheet") || m.includes("ms-excel") || m.includes("csv")
        || m.includes("officedocument") || m.includes("msword") || m.includes("ms-powerpoint") || m.includes("opendocument")
        || /\.(pdf|xlsx|xls|xlsm|ods|csv|txt|log|md|doc|docx|ppt|pptx|odt|odp|rtf)$/i.test(name);
}

function AttachmentCard({ attachment, editable, onDelete }) {
    const [preview, setPreview] = useState(false);
    const canPreview = !editable && channelPreviewable(attachment);
    const inner = (
        <Box sx={{
            display: "flex", alignItems: "center", gap: 1.2,
            p: "7px 11px", borderRadius: 1.5,
            border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
            maxWidth: 300, textDecoration: "none",
            "&:hover": { bgcolor: "#f1f5f9" },
            transition: "background 0.15s",
        }}>
            <Box sx={{ fontSize: 22, display: "flex", flexShrink: 0 }}>{fileIcon(attachment.contentType)}</Box>
            <Box flex={1} minWidth={0}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }} noWrap>
                    {attachment.originalFileName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{formatFileSize(attachment.fileSize)}</Typography>
            </Box>
            {editable ? (
                <IconButton size="small" onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onDelete(attachment.id); }} sx={{ p: 0.2 }}>
                    <CancelRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                </IconButton>
            ) : (
                <Stack direction="row" alignItems="center" spacing={0.2} sx={{ flexShrink: 0 }}>
                    {canPreview && (
                        <Tooltip title="미리보기">
                            <IconButton size="small"
                                onClick={e => { e.preventDefault(); e.stopPropagation(); setPreview(true); }}
                                sx={{ p: 0.3 }}>
                                <ZoomInRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <DownloadRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                </Stack>
            )}
        </Box>
    );
    if (editable) return inner;
    // stopPropagation — 행 클릭(피드 선택)과 다운로드 링크 클릭이 겹치지 않게.
    return (
        <>
            <Box component="a" href={attachment.fileUrl} target="_blank" onClick={e => e.stopPropagation()} sx={{ textDecoration: "none" }}>{inner}</Box>
            {canPreview && (
                <AttachmentPreviewModal
                    open={preview}
                    attachment={{ id: attachment.id, filename: attachment.originalFileName, mimeType: attachment.contentType }}
                    loader={() => channelApi.previewAttachment(attachment.id)}
                    onClose={() => setPreview(false)}
                />
            )}
        </>
    );
}

function splitAttachments(attachments = []) {
    const images = attachments.filter(a => a.contentType?.startsWith("image/"));
    const files  = attachments.filter(a => !a.contentType?.startsWith("image/"));
    return { images, files };
}

function ImageSlider({ images, editable, onDelete }) {
    const [idx, setIdx] = useState(0);
    const [preview, setPreview] = useState(false);
    if (!images.length) return null;
    const cur = images[Math.min(idx, images.length - 1)];
    const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); };
    const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); };

    return (
        <Box sx={{ position: "relative", mt: 1.5, borderRadius: 1.5, overflow: "hidden", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}>
            <Box
                component="img"
                src={cur.fileUrl}
                alt={cur.originalFileName}
                sx={{
                    display: "block",
                    width: "100%",
                    maxHeight: 360,
                    objectFit: "contain",
                    cursor: editable ? "default" : "pointer",
                }}
                onClick={e => { if (!editable) { e.stopPropagation(); setPreview(true); } }}
            />
            {/* 이미지 미리보기 — office 변환 안 타고 fileUrl 로 직접 표시(직접 URL 모드). */}
            {!editable && (
                <AttachmentPreviewModal
                    open={preview}
                    attachment={{ filename: cur.originalFileName, mimeType: cur.contentType, url: cur.fileUrl }}
                    onClose={() => setPreview(false)}
                />
            )}
            {editable && (
                <IconButton size="small"
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onDelete(cur.id); }}
                    sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(0,0,0,0.45)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.65)" } }}>
                    <CancelRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
            )}
            {images.length > 1 && (
                <>
                    <IconButton onClick={prev} sx={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(0,0,0,0.4)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.65)" } }}>
                        <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton onClick={next} sx={{ position: "absolute", right: editable ? 38 : 6, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(0,0,0,0.4)", color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(0,0,0,0.65)" } }}>
                        <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Box sx={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 0.6 }}>
                        {images.map((_, i) => (
                            <Box key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
                                sx={{ width: i === idx ? 14 : 6, height: 6, borderRadius: 3, bgcolor: i === idx ? "#fff" : "rgba(255,255,255,0.5)", cursor: "pointer", transition: "width 0.2s" }} />
                        ))}
                    </Box>
                    <Box sx={{ position: "absolute", top: 8, left: 8, bgcolor: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 11, px: 1, py: 0.2, borderRadius: 1 }}>
                        {idx + 1} / {images.length}
                    </Box>
                </>
            )}
        </Box>
    );
}

// data: URI를 포함한 이미지 src 허용
const DOMPURIFY_CFG = {
    ADD_ATTR: ["style"],
    ALLOWED_URI_REGEXP: /^(?:https?|ftp|blob|data:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp|svg\+xml))/i,
};

/**
 * sanitize 된 HTML 문자열 안에서 @홍길동 패턴만 파란 span 으로 감싼다.
 * 태그 속성/내부를 건드리지 않도록 '>...<' 사이의 텍스트 부분만 치환한다.
 * (DOMParser 로 textNode 순회하는 게 더 안전하지만, 본 페이지 본문 규모에선 정규식이면 충분)
 */
function highlightMentionsInHtml(html) {
    if (!html) return html;
    return html.replace(/>([^<]+)</g, (m, txt) =>
        ">" + txt.replace(
            /(@[\w가-힣\-]+)/g,
            '<span class="feed-mention">$1</span>'
        ) + "<"
    );
}

function RenderContent({ text, sx = {} }) {
    if (!text) return null;
    const isHtml = /<[a-z][\s\S]*>/i.test(text);
    if (isHtml) {
        const clean = DOMPurify.sanitize(text, DOMPURIFY_CFG);
        const withMentions = highlightMentionsInHtml(clean);
        return (
            <Box className="feed-content" sx={{
                ...sx,
                "& img": {
                    maxWidth: "100%",
                    maxHeight: 400,
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: "6px",
                    display: "block",
                    cursor: "pointer",
                },
                "& .feed-mention": {
                    color: "#2563eb",
                    fontWeight: 600,
                },
            }}
                dangerouslySetInnerHTML={{ __html: withMentions }} />
        );
    }
    const parts = text.split(/(@[\w가-힣\-]+)/g);
    return (
        <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.75, color: "#1e293b", ...sx }}>
            {parts.map((part, i) =>
                /^@/.test(part)
                    ? <Box key={i} component="span" sx={{ color: "#2563eb", fontWeight: 600 }}>{part}</Box>
                    : part
            )}
        </Typography>
    );
}

// 멘션 popper 최대 높이 — viewport flip 임계값으로도 사용.
const MENTION_POPPER_MAX_HEIGHT = 280;

function MentionInput({ value, onChange, onSubmit, placeholder, minRows = 2, members = [], sx = {} }) {
    const inputRef = useRef();
    const wrapperRef = useRef();
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    // popper 위치 — viewport 기준 fixed 좌표. Dialog/모달 안에서도 안 가려지도록 Portal+fixed.
    const [popperPos, setPopperPos] = useState({ left: 0, top: 0, openAbove: false });

    const mq = mentionQuery.toLowerCase();   // 대소문자 무시(영문 이름 멘션 검색 일관성)
    const filtered = members.filter(m => {
        const name = (m.employee?.name || m.name || "").toLowerCase();
        return name.includes(mq);
    }).slice(0, 8);

    const updatePopperPos = () => {
        if (!wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // 아래에 공간 부족하고 위에는 공간이 더 많으면 위로.
        const openAbove = spaceBelow < MENTION_POPPER_MAX_HEIGHT && spaceAbove > spaceBelow;
        setPopperPos({
            left: rect.left,
            top: openAbove ? rect.top : rect.bottom,
            openAbove,
        });
    };

    const handleChange = (e) => {
        const val = e.target.value;
        const cursor = e.target.selectionStart;
        onChange(val);
        const textBefore = val.slice(0, cursor);
        const match = textBefore.match(/@([^\s@]*)$/);
        if (match) {
            setMentionQuery(match[1]);
            updatePopperPos();
            setMentionOpen(true);
        } else {
            setMentionOpen(false);
        }
    };

    const handleSelect = (name) => {
        const cursor = inputRef.current?.selectionStart ?? value.length;
        const textBefore = value.slice(0, cursor);
        const match = textBefore.match(/@([^\s@]*)$/);
        if (match) {
            const before = value.slice(0, match.index);
            const after = value.slice(cursor);
            onChange(`${before}@${name} ${after}`);
        }
        setMentionOpen(false);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (mentionOpen && (e.key === "Escape" || e.key === " ")) setMentionOpen(false);
        if (e.key === "Enter" && !e.shiftKey && onSubmit) { e.preventDefault(); onSubmit(); }
    };

    return (
        <Box ref={wrapperRef} sx={{ position: "relative", ...sx }}>
            <TextField
                inputRef={inputRef}
                fullWidth multiline
                minRows={minRows} maxRows={10}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                variant="outlined"
                sx={{
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5, bgcolor: "#fff", fontSize: 14,
                        "& fieldset": { borderColor: "#e2e8f0" },
                        "&:hover fieldset": { borderColor: "#94a3b8" },
                        "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    }
                }}
            />
            {mentionOpen && filtered.length > 0 && (
                /* Dialog/모달 안에서도 안 가려지도록 Portal 로 body 직속에 렌더 + position:fixed.
                   부모의 stacking context / overflow:hidden 영향을 받지 않음. */
                <Portal>
                  <Paper elevation={4} sx={{
                    position: "fixed",
                    left: popperPos.left,
                    ...(popperPos.openAbove
                        ? { bottom: window.innerHeight - popperPos.top + 4 }
                        : { top: popperPos.top + 4 }),
                    zIndex: (theme) => theme.zIndex.modal + 100,
                    minWidth: 220, maxWidth: 320,
                    maxHeight: MENTION_POPPER_MAX_HEIGHT,
                    overflowY: "auto",
                    borderRadius: 1.5, border: "1px solid #e2e8f0",
                    boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
                  }}>
                    {filtered.map((m, i) => {
                        const emp = m.employee || m;
                        return (
                            <Box key={i} onMouseDown={e => { e.preventDefault(); handleSelect(emp.name); }}
                                sx={{ px: 1.5, py: 0.9, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.2, "&:hover": { bgcolor: "#f1f5f9" } }}>
                                <Avatar sx={{ width: 26, height: 26, fontSize: 11 }}>{emp.name?.[0]}</Avatar>
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{emp.name}</Typography>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                        {[emp.deptName, emp.positionName].filter(Boolean).join(" · ")}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                  </Paper>
                </Portal>
            )}
        </Box>
    );
}

function CommentItem({ comment, myId, channelId, members, onRefresh, highlight }) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.content);
    const fileInputRef = useRef();
    const rowRef = useRef();
    // 외부에서 highlight=true 가 들어오면 잠깐 강조 후 자동 페이드.
    // 두 단계로 나눈 이유: bgcolor 가 true→false 로 전이돼야 transition 으로 부드럽게 사라짐.
    const [glowing, setGlowing] = useState(false);
    useEffect(() => {
        if (!highlight) return;
        setGlowing(true);
        rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        const t = setTimeout(() => setGlowing(false), 1800);
        return () => clearTimeout(t);
    }, [highlight]);

    const handleSave = () => runApiAction({
        request: () => channelApi.updateComment(comment.id, {
            content: text,
            mentionedEmployeeCodes: extractMentionedEmployeeCodes(text, members),
        }),
        errorMsg: serverMsgOr("댓글 수정 실패"),
        onSuccess: () => { setEditing(false); onRefresh(); },
    });
    const handleDelete = async () => {
        if (!(await confirmDialog({ message: "댓글을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.deleteComment(comment.id),
            errorMsg: serverMsgOr("댓글 삭제 실패"),
            onSuccess: onRefresh,
        });
    };
    const handleDeleteAttachment = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.deleteAttachment(id),
            errorMsg: serverMsgOr("파일 삭제 실패"),
            onSuccess: onRefresh,
        });
    };
    const handleFileUpload = (e) => {
        const file = e.target.files[0]; if (!file) return;
        e.target.value = "";
        return runApiAction({
            request: () => channelApi.uploadFile(channelId, file, null, comment.id),
            errorMsg: serverMsgOr("파일 업로드 실패"),
            onSuccess: onRefresh,
        });
    };

    const isMine = comment.author?.id === myId;

    return (
        <Box ref={rowRef} sx={{
            px: 5, py: 0.75,
            transition: "background-color 0.8s ease",
            bgcolor: glowing ? "rgba(37, 99, 235, 0.10)" : "transparent",
            borderRadius: 1,
        }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, mt: 0.2, flexShrink: 0 }}>
                    {comment.author?.name?.[0]}
                </Avatar>
                <Box flex={1} minWidth={0}>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.3} flexWrap="wrap">
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{comment.author?.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{comment.author?.positionName}</Typography>
                        <Tooltip title={formatTime(comment.createdAt)} placement="top">
                            <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>{formatRelativeTime(comment.createdAt)}</Typography>
                        </Tooltip>
                        {comment.updatedAt && <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>(수정됨)</Typography>}
                    </Stack>
                    {editing ? (
                        <Stack spacing={0.75}>
                            <MentionInput value={text} onChange={setText} placeholder="댓글 내용..." minRows={1} members={withoutSelf(members, myId)} />
                            {(() => {
                                const { images, files: existFiles } = splitAttachments(comment.attachments);
                                return <>
                                    <ImageSlider images={images} editable onDelete={handleDeleteAttachment} />
                                    {existFiles.length > 0 && (
                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                            {existFiles.map(a => <AttachmentCard key={a.id} attachment={a} editable onDelete={handleDeleteAttachment} />)}
                                        </Stack>
                                    )}
                                </>;
                            })()}
                            <Stack direction="row" spacing={0.5}>
                                <ActionButton variant="save" onClick={handleSave} startIcon={false} sx={{ fontSize: 12, py: 0.3, minWidth: 0 }} />
                                <ActionButton variant="cancel" onClick={() => setEditing(false)} sx={{ fontSize: 12, py: 0.3, minWidth: 0 }} />
                            </Stack>
                        </Stack>
                    ) : (
                        <>
                            <RenderContent text={comment.content} />
                            {(() => {
                                const { images, files: nonImages } = splitAttachments(comment.attachments);
                                return <>
                                    <ImageSlider images={images} editable={false} />
                                    {nonImages.length > 0 && (
                                        <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                                            {nonImages.map(a => <AttachmentCard key={a.id} attachment={a} editable={false} />)}
                                        </Stack>
                                    )}
                                </>;
                            })()}
                        </>
                    )}
                </Box>
                {isMine && !editing && (
                    <Stack direction="row" sx={{ flexShrink: 0, mt: 0.2 }}>
                        <Tooltip title="파일 첨부">
                            <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ p: 0.4 }}>
                                <AttachFileRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="수정">
                            <IconButton size="small" onClick={() => setEditing(true)} sx={{ p: 0.4 }}>
                                <EditRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                            <IconButton size="small" onClick={handleDelete} sx={{ p: 0.4 }}>
                                <DeleteRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                            </IconButton>
                        </Tooltip>
                        <input ref={fileInputRef} type="file" hidden onChange={handleFileUpload} />
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}

// compact — 하단 댓글 독처럼 좁은 곳에서 아바타만 남기고 여백을 줄인 모드.
function CommentInput({ feedId, channelId, members, myId, onRefresh, compact = false }) {
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    // 댓글 + 첨부 업로드 진행 중 — 전송 버튼 안에 spinner 로 표시 (백드롭 없음).
    const [posting, setPosting] = useState(false);
    const fileInputRef = useRef();

    // 멤버 항목은 { id(멤버십 PK), employee:{ id, name, positionName } } 형태이므로
    // 본인 판정은 멤버십 id 가 아니라 employee.id 로 한다(withoutSelf 헬퍼와 동일 규약).
    const me = members?.find(m => (m.employee?.id ?? m.id) === myId);
    const meEmp = me?.employee ?? me;

    const handleSubmit = async () => {
        if (!text.trim() && !files.length) return;
        if (posting) return; // 중복 클릭 가드
        setPosting(true);
        try {
            const res = await channelApi.createComment(feedId, {
                content: text || " ",
                mentionedEmployeeCodes: extractMentionedEmployeeCodes(text, members),
            });
            const commentId = res.id;
            for (const f of files) await channelApi.uploadFile(channelId, f, null, commentId);
            setText(""); setFiles([]); onRefresh();
        } catch (err) {
            notify(getApiErrorMessage(err, "댓글 작성 실패"), "error");
        } finally {
            setPosting(false);
        }
    };

    return (
        <Box sx={{ px: compact ? 1.5 : 5, pt: compact ? 1 : 1.5, pb: compact ? 1.25 : 2 }}>
            {files.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} mb={0.75}>
                    {files.map((f, i) => <PendingFileChip key={i} file={f} onRemove={() => setFiles(files.filter((_, j) => j !== i))} />)}
                </Stack>
            )}
            <Stack direction="row" spacing={compact ? 1 : 1.5} alignItems="flex-start">
                {/* 내 아바타 + 이름/직책 (compact 는 아바타만) */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4, flexShrink: 0, pt: 0.5 }}>
                    <Avatar sx={{ width: compact ? 28 : 32, height: compact ? 28 : 32, fontSize: compact ? 12 : 13 }}>
                        {meEmp?.name?.[0] ?? "나"}
                    </Avatar>
                    {!compact && meEmp && (
                        <Box sx={{ textAlign: "center" }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#475569", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                {meEmp.name}
                            </Typography>
                            {meEmp.positionName && (
                                <Typography sx={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                                    {meEmp.positionName}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
                {/* 입력 영역 */}
                <Box flex={1} minWidth={0}>
                    <Stack direction="row" spacing={0.5} alignItems="flex-end">
                        <MentionInput
                            value={text} onChange={setText}
                            placeholder="댓글을 입력하세요... (@이름으로 멘션)"
                            minRows={1} members={withoutSelf(members, myId)} onSubmit={handleSubmit}
                            sx={{ flex: 1 }}
                            disabled={posting}
                        />
                        <Tooltip title="파일 첨부">
                            <span>
                                <IconButton size="small" onClick={() => fileInputRef.current?.click()}
                                    disabled={posting}
                                    sx={{ mb: 0.3, p: 0.6 }}>
                                    <AttachFileRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton size="small" onClick={handleSubmit}
                            disabled={posting || (!text.trim() && !files.length)}
                            sx={{ mb: 0.3, p: 0.6, color: (text.trim() || files.length) ? "#2563eb" : "#cbd5e1", transition: "color 0.15s" }}>
                            {posting
                                ? <CircularProgress size={16} sx={{ color: "#2563eb" }} />
                                : <SendRoundedIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <input ref={fileInputRef} type="file" hidden multiple onChange={e => setFiles(p => [...p, ...Array.from(e.target.files)])} />
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

function FeedDetailModal({ feed, myId, channelId, members, open, scrollToComments, highlightCommentId, onClose, onRefresh, onUpdate, onDelete, onToggleLike }) {
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(feed?.title || "");
    const [editText, setEditText] = useState(feed?.content || "");
    const [editEditorKey, setEditEditorKey] = useState(0);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef();
    const commentsEndRef = useRef();

    useEffect(() => {
        if (open) {
            setEditing(false);
            setEditTitle(feed?.title || "");
            setEditText(feed?.content || "");
            setEditEditorKey(k => k + 1);
            setPendingFiles([]);
        }
    }, [open, feed?.id]);

    useEffect(() => {
        if (open && scrollToComments) setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }, [feed?.comments?.length, open, scrollToComments]);

    if (!feed) return null;
    const isMine = feed.author?.id === myId;

    const handleSave = async () => {
        try {
            await onUpdate(feed.id, editTitle, editText);
            for (const f of pendingFiles) await channelApi.uploadFile(channelId, f, feed.id, null);
            setPendingFiles([]); setEditing(false);
        } catch (err) { notify(getApiErrorMessage(err, "피드 수정 실패"), "error"); }
    };

    const handleDeleteAttachment = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.deleteAttachment(id),
            errorMsg: serverMsgOr("파일 삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { maxHeight: "90vh", display: "flex", flexDirection: "column" } }}>
            {/* 모달 헤더 — 스크롤되는 본문과 고정 헤더를 가르는 선 하나는 남긴다(스크롤 중 경계 상실 방지). */}
            <Box sx={{ px: 5, py: 2, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Avatar sx={{ width: 40, height: 40, fontSize: 15, flexShrink: 0 }}>
                    {feed.author?.name?.[0]}
                </Avatar>
                <Box flex={1} minWidth={0}>
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{feed.author?.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                            {feed.author?.positionName}{feed.author?.deptName ? ` · ${feed.author.deptName}` : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{formatTime(feed.createdAt)}</Typography>
                        {feed.updatedAt && <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>(수정됨)</Typography>}
                    </Stack>
                    {feed.pinned && (
                        <Chip icon={<PushPinRoundedIcon sx={{ fontSize: 12 }} />} label="고정됨" size="small"
                            sx={{ mt: 0.5, fontSize: 11, height: 20, bgcolor: "#fef3c7", color: "#d97706", border: "1px solid #fde68a" }} />
                    )}
                </Box>
                {isMine && !editing && (
                    <>
                        <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
                            <MoreHorizRoundedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                            PaperProps={{ sx: { borderRadius: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 120 } }}
                            transformOrigin={{ horizontal: "right", vertical: "top" }}
                            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                            <MenuItem onClick={() => { setEditing(true); setEditTitle(feed.title || ""); setEditText(feed.content); setMenuAnchor(null); }}
                                sx={{ fontSize: 13, gap: 1 }}><EditRoundedIcon sx={{ fontSize: 16 }} /> 수정</MenuItem>
                            <MenuItem onClick={() => { onDelete(feed.id); setMenuAnchor(null); onClose(); }}
                                sx={{ fontSize: 13, gap: 1, color: "#ef4444" }}><DeleteRoundedIcon sx={{ fontSize: 16 }} /> 삭제</MenuItem>
                        </Menu>
                    </>
                )}
                <IconButton size="small" onClick={onClose} sx={{ p: 0.5 }}>
                    <CloseRoundedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, overflowY: "auto", flex: 1 }}>
                <Box sx={{ px: 5, py: 2.5 }}>
                    {editing ? (
                        <Stack spacing={1.5}>
                            <TextField fullWidth size="small" placeholder="제목 (선택)"
                                value={editTitle} onChange={e => setEditTitle(e.target.value)}
                                sx={{ "& .MuiOutlinedInput-root": { fontSize: 14 } }} />
                            <RichEditor key={editEditorKey} initialValue={editText} onChange={setEditText} placeholder="내용 수정..." minHeight={150}
                                onUploadImage={async (file) => { try { const r = await channelApi.uploadFile(channelId, file, null, null, null); return r.fileUrl; } catch { return null; } }} />
                            {(() => {
                                const { images, files: existFiles } = splitAttachments(feed.attachments);
                                return <>
                                    <ImageSlider images={images} editable onDelete={handleDeleteAttachment} />
                                    {existFiles.length > 0 && (
                                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                            {existFiles.map(a => <AttachmentCard key={a.id} attachment={a} editable onDelete={handleDeleteAttachment} />)}
                                        </Stack>
                                    )}
                                </>;
                            })()}
                            {pendingFiles.length > 0 && (
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {pendingFiles.map((f, i) => (
                                        <PendingFileChip key={i} file={f} onRemove={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))} />
                                    ))}
                                </Stack>
                            )}
                            <Stack direction="row" spacing={1} alignItems="center">
                                <ActionButton variant="save" onClick={handleSave} startIcon={false} />
                                <ActionButton variant="cancel" onClick={() => { setEditing(false); setPendingFiles([]); }} />
                                <Tooltip title="파일 추가">
                                    <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                                        <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                    </IconButton>
                                </Tooltip>
                                <input ref={fileInputRef} type="file" hidden multiple
                                    onChange={e => setPendingFiles(p => [...p, ...Array.from(e.target.files)])} />
                            </Stack>
                        </Stack>
                    ) : (
                        <>
                            {feed.title && (
                                <Typography sx={{ fontWeight: 700, fontSize: 17, color: C.textPrimary, mb: 1, lineHeight: 1.4 }}>
                                    {feed.title}
                                </Typography>
                            )}
                            <RenderContent text={feed.content} sx={{ fontSize: 15, lineHeight: 1.8 }} />
                            {(() => {
                                const { images, files: nonImages } = splitAttachments(feed.attachments);
                                return <>
                                    <ImageSlider images={images} editable={false} />
                                    {nonImages.length > 0 && (
                                        <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
                                            {nonImages.map(a => <AttachmentCard key={a.id} attachment={a} editable={false} />)}
                                        </Stack>
                                    )}
                                </>;
                            })()}
                        </>
                    )}
                </Box>
                {/* 좋아요 줄 — 본문과 한 덩어리라 사이에 선을 두지 않는다. */}
                <Box sx={{ px: 5, pb: 1.25 }}>
                    <LikeButton feed={feed} onToggle={onToggleLike} size={18} />
                </Box>
                {/* 댓글 영역 — 배경색 전환만으로 본문과 구분한다(회색 구분선 없이). */}
                <Box sx={{ bgcolor: C.bg, pb: 3 }}>
                    {feed.comments?.length > 0 && (
                        <Typography sx={{ px: 5, pt: 2, pb: 0.5, fontSize: 12, fontWeight: 700, color: C.textSecondary }}>
                            댓글 {feed.comments.length}개
                        </Typography>
                    )}
                    {feed.comments?.map(c => (
                        <CommentItem key={c.id} comment={c} myId={myId}
                         channelId={channelId} members={members} onRefresh={onRefresh}
                         highlight={highlightCommentId != null && Number(highlightCommentId) === c.id} />
                    ))}
                    <div ref={commentsEndRef} />
                </Box>
            </DialogContent>

            <Box sx={{ borderTop: "1px solid #e8ecf0", bgcolor: "#fff", pt: 1 }}>
                <CommentInput feedId={feed.id} channelId={channelId} members={members} myId={myId} onRefresh={onRefresh} />
            </Box>
        </Dialog>
    );
}

const MAX_CONTENT_LEN = 300;

/**
 * 디스코드식 피드 행.
 * - 카드가 아니라 컨테이너 안에서 가로선으로 구분되는 행이다 (구분선은 부모 컨테이너 CSS가 관리).
 * - 행 클릭 = 선택({@code onSelect}) → FeedTab 하단 댓글 독이 열린다. 재클릭 시 해제.
 * - 행 안에는 댓글 입력 섹션을 두지 않는다. 댓글이 있는 글만 💬 N pill 로 표시하고 클릭 시 상세 모달.
 */
function FeedItem({ feed, myId, channelId, members, onDelete, onUpdate, onPin, onRefresh, selected = false, onSelect, onToggleLike,
                   folders = [], onFolderChanged }) {
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(feed.title || "");
    const [editText, setEditText] = useState(feed.content);
    const [editEditorKey, setEditEditorKey] = useState(0);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [detailOpen, setDetailOpen] = useState(null); // null | "top" | "comments"
    const [expanded, setExpanded] = useState(false);
    // 폴더 이동 하위 메뉴의 anchor — 본 메뉴와 겹치지 않게 별도 상태로 둔다.
    const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
    const fileInputRef = useRef();

    const handleMoveFolder = (folderId) => {
        setFolderMenuAnchor(null);
        setMenuAnchor(null);
        return runApiAction({
            request: () => channelApi.moveFeedToFolder(feed.id, folderId),
            successMsg: folderId == null ? "미분류로 옮겼습니다" : "폴더를 옮겼습니다",
            errorMsg: serverMsgOr("폴더 이동 실패"),
            // 목록 재조회 + 칩 건수 갱신 — 옮긴 글은 현재 폴더에서 빠지므로 목록이 바뀐다.
            onSuccess: () => { onRefresh(); onFolderChanged?.(); },
        });
    };

    const isMine = feed.author?.id === myId;
    const plainContent = stripHtml(feed.content);
    const isLong = (plainContent?.length ?? 0) > MAX_CONTENT_LEN;
    const { images: feedImages, files: feedNonImages } = splitAttachments(feed.attachments);
    const hasInlineImages = /<img/i.test(feed.content || "");
    const needsMore = isLong || feedImages.length > 0 || feedNonImages.length > 0 || hasInlineImages;

    const handleSaveFeed = async () => {
        try {
            await onUpdate(feed.id, editTitle, editText);
            for (const f of pendingFiles) await channelApi.uploadFile(channelId, f, feed.id, null);
            setPendingFiles([]); setEditing(false);
        } catch (err) { notify(getApiErrorMessage(err, "피드 수정 실패"), "error"); }
    };

    const handleDeleteAttachment = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.deleteAttachment(id),
            errorMsg: serverMsgOr("파일 삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    const displayContent = feed.content;

    const commentCount = feed.comments?.length ?? 0;

    return (
        <>
            <Box
                onClick={editing ? undefined : () => setDetailOpen("comments")}
                sx={{
                    px: 2.5, py: 1.75,
                    cursor: editing ? "default" : "pointer",
                    "&:hover": { bgcolor: C.rowHover },
                    transition: "background 0.12s",
                }}
            >
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Avatar sx={{ width: 40, height: 40, fontSize: 15, flexShrink: 0, mt: 0.25 }}>
                        {feed.author?.name?.[0]}
                    </Avatar>

                    {/* 아바타 우측 — 이름줄 + 본문 (디스코드식 들여쓰기) */}
                    <Box flex={1} minWidth={0}>
                        {/* 이름줄: 이름 · 직책·부서 · 시간 · (수정됨) [고정] ... 핀/메뉴 */}
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary, flexShrink: 0 }}>
                                {feed.author?.name}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: C.textMuted, minWidth: 0 }} noWrap>
                                {[feed.author?.positionName, feed.author?.deptName].filter(Boolean).join(" · ")}
                                {(feed.author?.positionName || feed.author?.deptName) ? " · " : ""}
                                <Tooltip title={formatTime(feed.createdAt)} placement="top">
                                    <Box component="span">{formatRelativeTime(feed.createdAt)}</Box>
                                </Tooltip>
                                {feed.updatedAt ? " (수정됨)" : ""}
                            </Typography>
                            {feed.pinned && (
                                <Stack direction="row" alignItems="center" spacing={0.3}
                                    sx={{ bgcolor: C.primaryBg, border: `1px solid ${C.primaryLight}`, borderRadius: 1, px: 0.7, py: 0.1, flexShrink: 0 }}>
                                    <PushPinRoundedIcon sx={{ fontSize: 11, color: C.primary }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.primary }}>고정</Typography>
                                </Stack>
                            )}
                            {/*
                              * 폴더 배지 — '전체' 로 볼 때 이 글이 어느 파트 것인지 알려주는 유일한 표시.
                              *
                              * 폴더가 없는 글에는 <b>'미분류'</b> 를 붙인다. 배지가 아예 없으면 "폴더를 안 정한 글"
                              * 과 "폴더 기능을 안 쓰는 채널의 글" 이 똑같아 보여, 정리해야 할 글을 찾을 수 없다.
                              * 단 <b>폴더를 쓰는 채널에서만</b> 붙인다 — 폴더가 없는 채널에서는 모든 글에
                              * '미분류' 가 달려 아무 정보도 주지 못한다.
                              */}
                            {feed.folderName ? (
                                <Stack direction="row" alignItems="center" spacing={0.3}
                                    sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: 1, px: 0.7, py: 0.1, flexShrink: 0 }}>
                                    <FolderRoundedIcon sx={{ fontSize: 11, color: "#f59e0b" }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.textSecondary }}>
                                        {feed.folderName}
                                    </Typography>
                                </Stack>
                            ) : folders.length > 0 && (
                                <Tooltip title="폴더를 지정하지 않은 글입니다">
                                    <Stack direction="row" alignItems="center" spacing={0.3}
                                        sx={{ bgcolor: C.bg, border: `1px dashed ${C.border}`, borderRadius: 1, px: 0.7, py: 0.1, flexShrink: 0 }}>
                                        <FolderRoundedIcon sx={{ fontSize: 11, color: "#cbd5e1" }} />
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.textMuted }}>
                                            미분류
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                            )}
                            <Box flex={1} />
                            {!editing && isMine && (
                                <>
                                    <Tooltip title={feed.pinned ? "고정 해제" : "상단 고정"}>
                                        <IconButton size="small" onClick={e => { e.stopPropagation(); onPin(feed.id); }} sx={{ p: 0.4, flexShrink: 0 }}>
                                            {feed.pinned
                                                ? <PushPinRoundedIcon sx={{ fontSize: 16, color: C.primary }} />
                                                : <PushPinOutlinedIcon sx={{ fontSize: 16, color: "#cbd5e1", "&:hover": { color: C.textMuted } }} />}
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }} sx={{ p: 0.4, flexShrink: 0 }}>
                                        <MoreHorizRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />
                                    </IconButton>
                                    {/* Menu 는 portal 이지만 React 트리로는 행의 자식 — 클릭이 행 선택으로 버블되지 않게 차단 */}
                                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                                        onClick={e => e.stopPropagation()}
                                        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 110 } }}
                                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                                        <MenuItem onClick={() => { setEditing(true); setEditTitle(feed.title || ""); setEditText(feed.content || ""); setEditEditorKey(k => k + 1); setMenuAnchor(null); }}
                                            sx={{ fontSize: 13, gap: 1 }}>
                                            <EditRoundedIcon sx={{ fontSize: 16 }} /> 수정
                                        </MenuItem>
                                        {/* 폴더를 쓰는 채널에서만 — 폴더가 없으면 옮길 곳도 없다. */}
                                        {folders.length > 0 && (
                                            <MenuItem onClick={e => setFolderMenuAnchor(e.currentTarget)}
                                                sx={{ fontSize: 13, gap: 1 }}>
                                                <FolderRoundedIcon sx={{ fontSize: 16 }} /> 폴더 이동
                                            </MenuItem>
                                        )}
                                        <MenuItem onClick={() => { onDelete(feed.id); setMenuAnchor(null); }}
                                            sx={{ fontSize: 13, gap: 1, color: "#ef4444" }}>
                                            <DeleteRoundedIcon sx={{ fontSize: 16 }} /> 삭제
                                        </MenuItem>
                                    </Menu>
                                    <Menu anchorEl={folderMenuAnchor} open={Boolean(folderMenuAnchor)}
                                        onClose={() => setFolderMenuAnchor(null)}
                                        onClick={e => e.stopPropagation()}
                                        slotProps={{ paper: { sx: { borderRadius: 1.5, minWidth: 160 } } }}>
                                        <MenuItem onClick={() => handleMoveFolder(null)}
                                            selected={!feed.folderId}
                                            sx={{ fontSize: 13 }}>미분류</MenuItem>
                                        {folders.map(f => (
                                            <MenuItem key={f.id} onClick={() => handleMoveFolder(f.id)}
                                                selected={feed.folderId === f.id}
                                                sx={{ fontSize: 13 }}>{f.name}</MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            )}
                        </Stack>

                        {/* 본문 */}
                        {editing ? (
                            <Stack spacing={1.2} mt={1}>
                                <TextField fullWidth size="small" placeholder="제목 (선택)"
                                    value={editTitle} onChange={e => setEditTitle(e.target.value)}
                                    sx={{ "& .MuiOutlinedInput-root": { fontSize: 14 } }} />
                                <RichEditor key={editEditorKey} initialValue={editText} onChange={setEditText} placeholder="내용을 입력하세요..." minHeight={120}
                                    onUploadImage={async (file) => { try { const r = await channelApi.uploadFile(channelId, file, null, null, null); return r.fileUrl; } catch { return null; } }} />
                                {(() => {
                                    const { images, files: existFiles } = splitAttachments(feed.attachments);
                                    return <>
                                        <ImageSlider images={images} editable onDelete={handleDeleteAttachment} />
                                        {existFiles.length > 0 && (
                                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                                {existFiles.map(a => <AttachmentCard key={a.id} attachment={a} editable onDelete={handleDeleteAttachment} />)}
                                            </Stack>
                                        )}
                                    </>;
                                })()}
                                {pendingFiles.length > 0 && (
                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                        {pendingFiles.map((f, i) => <PendingFileChip key={i} file={f} onRemove={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))} />)}
                                    </Stack>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <ActionButton variant="save" onClick={handleSaveFeed} startIcon={false} />
                                    <ActionButton variant="cancel" onClick={() => { setEditing(false); setPendingFiles([]); }} />
                                    <Tooltip title="파일 추가">
                                        <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                                            <AttachFileRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                                        </IconButton>
                                    </Tooltip>
                                    <input ref={fileInputRef} type="file" hidden multiple
                                        onChange={e => setPendingFiles(p => [...p, ...Array.from(e.target.files)])} />
                                </Stack>
                            </Stack>
                        ) : (
                            <>
                                {feed.title && (
                                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.textPrimary, mt: 0.5, lineHeight: 1.45 }}>
                                        {feed.title}
                                    </Typography>
                                )}
                                <Box sx={{
                                    mt: feed.title ? 0.25 : 0.5,
                                    maxHeight: expanded ? "none" : "220px",
                                    overflow: "hidden",
                                    position: "relative",
                                }}>
                                    <RenderContent text={displayContent} sx={{ fontSize: 14, lineHeight: 1.7 }} />
                                    {needsMore && !expanded && (
                                        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 56,
                                            background: `linear-gradient(transparent, ${selected ? "#eff6ff" : "#fff"})`,
                                            pointerEvents: "none" }} />
                                    )}
                                </Box>

                                {/* 이미지 미리보기: 축소 시 1장 (클릭 = 인라인 펼침), 펼침 시 전체 슬라이더 */}
                                {!expanded && feedImages.length > 0 && (
                                    <Box onClick={e => { e.stopPropagation(); setExpanded(true); }} sx={{
                                        mt: 1.25, borderRadius: 1.5, overflow: "hidden", maxWidth: 560,
                                        border: `1px solid ${C.border}`, bgcolor: C.borderLight,
                                        cursor: "pointer", position: "relative",
                                    }}>
                                        <Box
                                            component="img"
                                            src={feedImages[0].fileUrl}
                                            alt={feedImages[0].originalFileName}
                                            sx={{ display: "block", width: "100%", maxHeight: 180, objectFit: "contain" }}
                                        />
                                        <Box sx={{
                                            position: "absolute", bottom: 0, left: 0, right: 0, height: 64,
                                            background: "linear-gradient(transparent, rgba(241,245,249,0.95))",
                                            pointerEvents: "none",
                                        }} />
                                        {feedImages.length > 1 && (
                                            <Box sx={{
                                                position: "absolute", top: 6, right: 6,
                                                bgcolor: "rgba(0,0,0,0.45)", color: "#fff",
                                                fontSize: 11, px: 0.9, py: 0.2, borderRadius: 1,
                                                fontWeight: 600,
                                            }}>
                                                +{feedImages.length - 1}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                {expanded && feedImages.length > 0 && (
                                    <Box sx={{ maxWidth: 640 }}>
                                        <ImageSlider images={feedImages} editable={false} />
                                    </Box>
                                )}
                                {expanded && feedNonImages.length > 0 && (
                                    <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
                                        {feedNonImages.map(a => <AttachmentCard key={a.id} attachment={a} editable={false} />)}
                                    </Stack>
                                )}

                                {needsMore && (
                                    <Typography
                                        onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
                                        sx={{ display: "inline-block", mt: 0.75, fontSize: 13, fontWeight: 600,
                                            color: C.secondary, cursor: "pointer",
                                            "&:hover": { color: C.textPrimary } }}>
                                        {expanded ? "접기" : "더보기"}
                                    </Typography>
                                )}

                                {/* 액션 바 — 좋아요 + 댓글(모달) + 첨부 */}
                                <Stack direction="row" alignItems="center" spacing={2.25} mt={1} onClick={e => e.stopPropagation()}>
                                    <LikeButton feed={feed} onToggle={onToggleLike} />
                                    <Stack direction="row" alignItems="center" spacing={0.4}
                                        onClick={() => setDetailOpen("comments")}
                                        sx={{ cursor: "pointer", color: C.textMuted, "&:hover": { color: C.primary } }}>
                                        <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 15 }} />
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "inherit" }}>댓글 {commentCount}</Typography>
                                    </Stack>
                                    {feedNonImages.length > 0 && !expanded && (
                                        <Stack direction="row" alignItems="center" spacing={0.4}
                                            onClick={() => setExpanded(true)}
                                            sx={{ cursor: "pointer", color: C.textMuted, "&:hover": { color: C.textPrimary } }}>
                                            <AttachFileRoundedIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12 }}>첨부 {feedNonImages.length}</Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </>
                        )}
                    </Box>
                </Stack>
            </Box>
            <FeedDetailModal
                feed={feed} myId={myId} channelId={channelId} members={members}
                open={Boolean(detailOpen)} scrollToComments={detailOpen === "comments"}
                onClose={() => setDetailOpen(null)}
                onRefresh={onRefresh} onUpdate={onUpdate} onDelete={onDelete} onToggleLike={onToggleLike}
            />
        </>
    );
}

/**
 * 목록형 피드 아이템 — 인스타식 피드형 대신 게시판처럼 한 줄로 요약.
 * 클릭 시 기존 {@link FeedDetailModal} 을 열어 본문/댓글/첨부를 모두 보여줌 (피드형과 동일한 상세 UX).
 *
 * 한 줄 구성: [공지 chip] · 제목(또는 본문 발췌) · [첨부 클립] · [댓글수 chip] · 작성자 · 날짜
 */
function FeedListItem({ feed, myId, channelId, members, onDelete, onUpdate, onPin, onRefresh, onToggleLike }) {
    const [detailOpen, setDetailOpen] = useState(false);
    const isPinned = feed.pinned;
    const hasAttachments = (feed.attachments?.length ?? 0) > 0;
    const commentCount = feed.comments?.length ?? feed.commentCount ?? 0;
    const titleText = feed.title || stripHtml(feed.content) || "(제목 없음)";
    const dateStr = feed.createdAt ? new Date(feed.createdAt).toLocaleDateString("ko-KR", {
        year: "2-digit", month: "2-digit", day: "2-digit",
    }).replace(/\. /g, "-").replace(".", "") : "";

    return (
        <>
            <Box
                onClick={() => setDetailOpen(true)}
                sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    py: 1.1, px: 1.5, cursor: "pointer",
                    bgcolor: isPinned ? "#fffbeb" : C.surface,
                    // 행마다 borderBottom 을 두지 않는다 — 목록이 20줄이면 회색 줄도 20개가 된다.
                    "&:hover": { bgcolor: isPinned ? "#fef3c7" : C.bg },
                    transition: "background-color 0.12s",
                }}
            >
                {/* 좌측 — 제목 + 첨부 + 댓글수 */}
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.6 }}>
                    {isPinned && (
                        <Chip label="공지" size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#fde68a", color: "#92400e" }} />
                    )}
                    {feed.feedType === "POLL" && (
                        <Chip label="투표" size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#dbeafe", color: "#1e40af" }} />
                    )}
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b", minWidth: 0 }}>
                        {titleText}
                    </Typography>
                    {hasAttachments && (
                        <AttachFileRoundedIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
                    )}
                    {commentCount > 0 && (
                        <Typography sx={{ fontSize: 12, color: C.primary, fontWeight: 700, flexShrink: 0 }}>
                            [{commentCount}]
                        </Typography>
                    )}
                    <Box onClick={e => { e.stopPropagation(); onToggleLike?.(feed.id); }}
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, flexShrink: 0, cursor: "pointer",
                            color: feed.likedByMe ? C.error : C.textMuted, "&:hover": { color: C.error } }}>
                        {feed.likedByMe ? <FavoriteRoundedIcon sx={{ fontSize: 13 }} /> : <FavoriteBorderRoundedIcon sx={{ fontSize: 13 }} />}
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "inherit" }}>{feed.likeCount ?? 0}</Typography>
                    </Box>
                </Box>

                {/* 우측 — 작성자 + 날짜 */}
                <Typography sx={{ fontSize: 12, color: "#64748b", flexShrink: 0,
                    width: { xs: 70, sm: 100 }, textAlign: "right" }} noWrap>
                    {feed.author?.name || "—"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0, width: 72, textAlign: "right" }}>
                    {dateStr}
                </Typography>
            </Box>

            {detailOpen && feed.feedType !== "POLL" && (
                <FeedDetailModal
                    feed={feed} myId={myId} channelId={channelId} members={members}
                    open={detailOpen}
                    onClose={() => setDetailOpen(false)}
                    onRefresh={onRefresh}
                    onUpdate={onUpdate}
                    onDelete={(...args) => { onDelete?.(...args); setDetailOpen(false); }}
                    onToggleLike={onToggleLike}
                />
            )}
        </>
    );
}

/** 투표 피드 아이템 */
function PollFeedItem({ feed, myId, onDelete, onPin, onRefresh, onToggleLike }) {
    const isMine = feed.author?.id === myId;
    const [selected, setSelected] = useState(
        () => feed.pollOptions?.filter(o => o.votedByMe).map(o => o.id) ?? []
    );
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [voting, setVoting] = useState(false);

    useEffect(() => {
        setSelected(feed.pollOptions?.filter(o => o.votedByMe).map(o => o.id) ?? []);
    }, [feed.id, feed.hasVoted]);

    const expired = feed.expired;
    const totalVotes = feed.canSeeResult
        ? (feed.pollOptions?.reduce((s, o) => s + (o.voteCount ?? 0), 0) ?? 0)
        : 0;

    const originalSelected = feed.pollOptions?.filter(o => o.votedByMe).map(o => o.id) ?? [];
    const selSorted = [...selected].sort().join();
    const origSorted = [...originalSelected].sort().join();
    const hasChanged = selSorted !== origSorted;
    const canSubmit = selected.length > 0 && (!feed.hasVoted || hasChanged);

    const toggleOption = (optId) => {
        if (expired) return;
        if (feed.multiChoice) {
            setSelected(prev => prev.includes(optId) ? prev.filter(id => id !== optId) : [...prev, optId]);
        } else {
            setSelected([optId]);
        }
    };

    const handleVote = () => {
        if (!canSubmit) return;
        return runApiAction({
            request: () => channelApi.vote(feed.id, { optionIds: selected }),
            errorMsg: serverMsgOr("투표 실패"),
            setLoading: setVoting,
            onSuccess: onRefresh,
        });
    };

    const handleCancelVote = () => runApiAction({
        request: () => channelApi.cancelVote(feed.id),
        errorMsg: serverMsgOr("투표 취소 실패"),
        onSuccess: onRefresh,
    });

    const handleClosePoll = async () => {
        if (!(await confirmDialog({ message: "투표를 마감하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.closePoll(feed.id),
            errorMsg: serverMsgOr("마감 처리 실패"),
            onSuccess: onRefresh,
        });
    };

    return (
        <Box sx={{
            px: 2.5, py: 1.75,
            "&:hover": { bgcolor: "#fafbfc" },
            transition: "background 0.12s",
        }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Avatar sx={{ width: 40, height: 40, fontSize: 15, flexShrink: 0, mt: 0.25 }}>
                    {feed.author?.name?.[0]}
                </Avatar>

                {/* 아바타 우측 — 이름줄 + 투표 본문 (디스코드식 들여쓰기) */}
                <Box flex={1} minWidth={0}>
                    {/* 이름줄: 이름 [투표][마감] 직책·부서·시간 [고정] ... 핀/메뉴 */}
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary, flexShrink: 0 }}>
                            {feed.author?.name}
                        </Typography>
                        <Chip
                            icon={<BallotRoundedIcon sx={{ fontSize: 12, "&&": { color: C.primary } }} />}
                            label="투표"
                            size="small"
                            sx={{ height: 20, fontSize: 11, bgcolor: C.primaryLight, color: C.primary, flexShrink: 0, "& .MuiChip-label": { px: 0.8 }, "& .MuiChip-icon": { ml: 0.5 } }}
                        />
                        {expired && (
                            <Chip label="마감" size="small"
                                sx={{ height: 20, fontSize: 11, bgcolor: C.errorBg, color: C.error, flexShrink: 0, "& .MuiChip-label": { px: 0.8 } }} />
                        )}
                        <Typography sx={{ fontSize: 12, color: C.textMuted, minWidth: 0 }} noWrap>
                            {[feed.author?.positionName, feed.author?.deptName].filter(Boolean).join(" · ")}
                            {(feed.author?.positionName || feed.author?.deptName) ? " · " : ""}
                            <Tooltip title={formatTime(feed.createdAt)} placement="top">
                                <Box component="span">{formatRelativeTime(feed.createdAt)}</Box>
                            </Tooltip>
                        </Typography>
                        {feed.pinned && (
                            <Stack direction="row" alignItems="center" spacing={0.3}
                                sx={{ bgcolor: C.primaryBg, border: `1px solid ${C.primaryLight}`, borderRadius: 1, px: 0.7, py: 0.1, flexShrink: 0 }}>
                                <PushPinRoundedIcon sx={{ fontSize: 11, color: C.primary }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.primary }}>고정</Typography>
                            </Stack>
                        )}
                        <Box flex={1} />
                        {isMine && (
                            <>
                                <Tooltip title={feed.pinned ? "고정 해제" : "상단 고정"}>
                                    <IconButton size="small" onClick={() => onPin(feed.id)} sx={{ p: 0.4, flexShrink: 0 }}>
                                        {feed.pinned
                                            ? <PushPinRoundedIcon sx={{ fontSize: 16, color: C.primary }} />
                                            : <PushPinOutlinedIcon sx={{ fontSize: 16, color: "#cbd5e1" }} />}
                                    </IconButton>
                                </Tooltip>
                                <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)} sx={{ p: 0.4, flexShrink: 0 }}>
                                    <MoreHorizRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />
                                </IconButton>
                                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                                    PaperProps={{ sx: { borderRadius: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 110 } }}
                                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                                    <MenuItem onClick={() => { onDelete(feed.id); setMenuAnchor(null); }}
                                        sx={{ fontSize: 13, gap: 1, color: "#ef4444" }}>
                                        <DeleteRoundedIcon sx={{ fontSize: 16 }} /> 삭제
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Stack>

                    {/* 질문 */}
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.textPrimary, mt: 0.5, mb: 1.25, lineHeight: 1.45 }}>
                        {feed.question}
                    </Typography>

                {/* 설정 뱃지 */}
                <Stack direction="row" spacing={0.5} mb={1.5} flexWrap="wrap" useFlexGap>
                    {feed.anonymous && (
                        <Chip label="익명" size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: C.successBg, color: C.success, "& .MuiChip-label": { px: 0.8 } }} />
                    )}
                    {feed.multiChoice && (
                        <Chip label="복수선택" size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: C.primaryBg, color: C.primary, "& .MuiChip-label": { px: 0.8 } }} />
                    )}
                    {feed.privateResult && (
                        <Chip label="결과 나만 보기" size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: C.warningBg, color: C.warning, "& .MuiChip-label": { px: 0.8 } }} />
                    )}
                    {feed.deadline && !expired && (
                        <Chip label={`~${dayjs(feed.deadline).format("MM/DD HH:mm")}`} size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: C.bg, color: C.secondary, "& .MuiChip-label": { px: 0.8 } }} />
                    )}
                </Stack>

                {/* 선택지 목록 — 넓은 화면에서 과하게 늘어나지 않게 폭 제한 */}
                <Stack spacing={0.75} mb={1.5} sx={{ maxWidth: 640 }}>
                    {feed.pollOptions?.map(opt => {
                        const isSelected = selected.includes(opt.id);
                        const pct = feed.canSeeResult && totalVotes > 0
                            ? Math.round(((opt.voteCount ?? 0) / totalVotes) * 100) : 0;
                        const showResult = feed.canSeeResult && feed.hasVoted;

                        return (
                            <Box key={opt.id} onClick={() => toggleOption(opt.id)}
                                sx={{
                                    position: "relative",
                                    border: `1px solid ${isSelected ? C.primary : C.border}`,
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    cursor: expired ? "default" : "pointer",
                                    bgcolor: isSelected ? C.primaryBg : C.surface,
                                    transition: "border-color 0.15s, background 0.15s",
                                    "&:hover": !expired ? { borderColor: C.primarySoft } : {},
                                    userSelect: "none",
                                }}>
                                {/* 결과 진행 바 (배경) */}
                                {showResult && (
                                    <Box sx={{
                                        position: "absolute", top: 0, left: 0,
                                        height: "100%", width: `${pct}%`,
                                        bgcolor: isSelected ? "rgba(37,99,235,0.12)" : "rgba(148,163,184,0.1)",
                                        transition: "width 0.4s ease",
                                    }} />
                                )}
                                <Box sx={{ position: "relative", px: 1.5, py: 0.9, display: "flex", alignItems: "center", gap: 1 }}>
                                    {/* 선택 아이콘 */}
                                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                                        {feed.multiChoice
                                            ? (isSelected
                                                ? <CheckBoxRoundedIcon sx={{ fontSize: 17, color: C.primary }} />
                                                : <CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 17, color: "#cbd5e1" }} />)
                                            : (isSelected
                                                ? <CheckCircleRoundedIcon sx={{ fontSize: 17, color: C.primary }} />
                                                : <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 17, color: "#cbd5e1" }} />)
                                        }
                                    </Box>
                                    <Typography sx={{ fontSize: 13, color: isSelected ? C.textPrimary : "#374151", flex: 1, fontWeight: isSelected ? 600 : 400, lineHeight: 1.4 }}>
                                        {opt.optionText}
                                    </Typography>
                                    {showResult && (
                                        <Typography sx={{ fontSize: 12, color: isSelected ? C.primary : C.secondary, fontWeight: isSelected ? 700 : 400, flexShrink: 0, minWidth: 34, textAlign: "right" }}>
                                            {pct}%
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>

                {/* 참여자 수 */}
                {feed.canSeeResult && (
                    <Typography sx={{ fontSize: 12, color: C.textMuted, mb: 1.2 }}>
                        {feed.totalVoters}명 참여
                    </Typography>
                )}

                {/* 투표 버튼 */}
                {!expired && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ActionButton variant="primary"
                            onClick={handleVote}
                            disabled={!canSubmit}
                            loading={voting}
                            sx={{ fontSize: 12, px: 2 }}
                            label={feed.hasVoted ? "투표 변경" : "투표하기"} />
                        {feed.hasVoted && (
                            <ActionButton variant="secondary"
                                startIcon={false}
                                onClick={handleCancelVote}
                                sx={{ fontSize: 12, color: C.secondary, borderColor: "transparent",
                                    "&:hover": { borderColor: "transparent", bgcolor: C.bg } }}
                                label="투표 취소" />
                        )}
                        {isMine && (
                            <ActionButton variant="secondary"
                                startIcon={false}
                                onClick={handleClosePoll}
                                sx={{ fontSize: 12, color: "#ef4444", borderColor: "transparent",
                                    "&:hover": { borderColor: "transparent", bgcolor: C.errorBg } }}
                                label="마감하기" />
                        )}
                    </Stack>
                )}
                {expired && feed.canSeeResult && (
                    <Typography sx={{ fontSize: 12, color: C.textMuted, fontStyle: "italic" }}>
                        투표가 마감되었습니다.
                    </Typography>
                )}
                <Box sx={{ mt: 1.5 }}>
                    <LikeButton feed={feed} onToggle={onToggleLike} />
                </Box>
                </Box>
            </Stack>
        </Box>
    );
}

function PinnedList({ pinnedFeeds, myId, onOpenDetail, onPin }) {
    if (!pinnedFeeds.length) return null;
    return (
        <Paper elevation={0} sx={{ border: `1px solid ${C.borderLight}`, borderRadius: 1.5, overflow: "hidden", bgcolor: C.surface }}>
            {pinnedFeeds.map((feed) => {
                // POLL이면 질문, 일반 글이면 제목 or 본문 미리보기
                const preview = feed.feedType === "POLL"
                    ? feed.question
                    : (feed.title || stripHtml(feed.content));
                return (
                    <Box key={feed.id}
                        onClick={() => feed.feedType !== "POLL" && onOpenDetail(feed)}
                        sx={{
                            display: "flex", alignItems: "center", gap: 1.25,
                            px: 2, py: 0.9,
                            cursor: feed.feedType !== "POLL" ? "pointer" : "default",
                            "&:hover": feed.feedType !== "POLL" ? { bgcolor: C.bg } : {},
                            transition: "background 0.1s",
                        }}>
                        <PushPinRoundedIcon sx={{ fontSize: 14, color: "#f59e0b", flexShrink: 0 }} />
                        {feed.feedType === "POLL" && (
                            <BallotRoundedIcon sx={{ fontSize: 14, color: C.primary, flexShrink: 0 }} />
                        )}
                        <Typography sx={{
                            fontSize: 13, fontWeight: 500, color: C.textPrimary, flex: 1,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                            {preview}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }}>
                            {feed.author?.name}
                        </Typography>
                        {feed.author?.id === myId && (
                            <Tooltip title="고정 해제">
                                <IconButton size="small"
                                    onClick={e => { e.stopPropagation(); onPin(feed.id); }}
                                    sx={{ p: 0.2, flexShrink: 0, "&:hover": { bgcolor: C.errorBg } }}>
                                    <CloseRoundedIcon sx={{ fontSize: 12, color: C.textMuted }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            })}
        </Paper>
    );
}

// ComposeTabBtn / PollSettingRow → FeedComposeModal 로 이전.

export default function FeedTab({
    channelId, myId, members = [], onRead, onChannelRefresh,
    /** 조회 범위 — {} (전체) | { folderId } | { onlyUnfiled: true } */
    folderScope = {},
    /** 새 글의 기본 폴더 — '전체'·'미분류' 를 보고 있으면 null(미분류로 등록) */
    composeFolderId = null,
    /** 항목별 '폴더 이동' 메뉴 선택지 */
    folders = [],
    /** 폴더 이동 후 칩 건수 갱신 */
    onFolderChanged,
    /** 딥링크로 열린 글이 현재 폴더 밖일 때 — 폴더 선택을 '전체'로 되돌려 달라는 요청 */
    onRequestAllFolders,
}) {
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    // 보기 모드 — "feed" (인스타식 카드) | "list" (게시판식 한 줄). channelId 별 localStorage 에 저장.
    const VIEW_MODE_KEY = `channel:viewMode:${channelId}`;
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem(VIEW_MODE_KEY) || "feed"; } catch { return "feed"; }
    });
    useEffect(() => {
        try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);
    // 채널 전환 시 해당 채널의 저장값 복원.
    useEffect(() => {
        try { setViewMode(localStorage.getItem(VIEW_MODE_KEY) || "feed"); } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId]);
    /**
     * 폴더 범위를 useEffect 의존성으로 쓰기 위한 문자열 키.
     * folderScope 는 매 렌더 새 객체라 그대로 넣으면 무한 재조회가 된다.
     */
    const folderKey = JSON.stringify(folderScope);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const sentinelRef = useRef(null);
    const [composeOpen, setComposeOpen] = useState(false);
    const [myInfo, setMyInfo] = useState(null);
    const [pinnedDetail, setPinnedDetail] = useState(null);
    // 알람/deep link 진입으로 자동 오픈된 피드. {feed, commentId} | null
    const [deepLinkDetail, setDeepLinkDetail] = useState(null);
    // 디스코드식 행 선택 — 선택된 피드가 있을 때만 하단 댓글 독(dock)이 뜬다.
    const [selectedFeedId, setSelectedFeedId] = useState(null);
    // 독의 "댓글 N개 보기" → 상세 모달
    const [dockDetailOpen, setDockDetailOpen] = useState(false);
    // 작성 폼 상태(제목·본문·첨부·투표 옵션 등)는 FeedComposeModal 가 직접 소유.

    // 첫 페이지 로드 (새로고침 / 채널 전환).
    // showSpinner=false 면 setLoading 을 건너뛴다 — 댓글/수정 후 갱신처럼 본문 화면 유지가 필요한 경우.
    // (setLoading(true) 가 발생하면 FeedTab 자체가 LoadingDots 로 리렌더되며 FeedItem 들이 unmount → 열려있던
    //  detailOpen 같은 자식 state 가 모두 사라져 모달이 꺼진다.)
    const load = async ({ showSpinner = true } = {}) => {
        if (showSpinner) setLoading(true);
        try {
            const data = await channelApi.getFeeds(channelId, 0, 10, folderScope);
            // 페이지네이션 응답 vs 구버전 배열 응답 모두 대응
            if (Array.isArray(data)) {
                setFeeds(data);
                setHasNext(false);
            } else {
                setFeeds(Array.isArray(data.feeds) ? data.feeds : []);
                setHasNext(data.hasNext ?? false);
            }
            setPage(0);
            onRead?.(channelId);
        } catch (error) { notify(error.message || "피드 불러오기 실패", "error"); }
        finally { if (showSpinner) setLoading(false); }
    };
    const silentRefresh = () => load({ showSpinner: false });

    // 다음 페이지 추가 로드
    const loadMore = async () => {
        if (loadingMore || !hasNext) return;
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const data = await channelApi.getFeeds(channelId, nextPage, 10, folderScope);
            const newFeeds = Array.isArray(data.feeds) ? data.feeds : [];
            setFeeds(prev => {
                const existingIds = new Set(prev.map(f => f.id));
                return [...prev, ...newFeeds.filter(f => !existingIds.has(f.id))];
            });
            setHasNext(data.hasNext ?? false);
            setPage(nextPage);
        } catch (error) { notify(error.message || "피드 추가 로드 실패", "error"); }
        finally { setLoadingMore(false); }
    };

    // 채널 전환뿐 아니라 <b>폴더 전환</b>에도 목록을 처음부터 다시 읽는다 —
    // 폴더를 바꿨는데 이전 폴더의 글이 남아 있으면 어느 폴더를 보고 있는지 알 수 없다.
    useEffect(() => {
        setFeeds([]);
        setPage(0);
        setHasNext(false);
        setSelectedFeedId(null);
        setDockDetailOpen(false);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId, folderKey]);

    useEffect(() => {
        api.get(`${API_BASE}/employees/me`).then(d => setMyInfo(d)).catch(() => {});
    }, []);

    // Esc 로 행 선택(하단 댓글 독) 해제
    useEffect(() => {
        if (!selectedFeedId) return;
        const onKey = (e) => { if (e.key === "Escape") setSelectedFeedId(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedFeedId]);

    /* 알람·외부 deep link 진입 — ?openFeed=<id>&commentId=<cid>
       단건 피드 fetch 후 모달 자동 오픈. commentId 가 있으면 CommentItem 에 highlight 전달.
       location.search 변경 시마다 동작 → 같은 페이지에서 다른 알람·같은 알람 재클릭 모두 정상.
       처리 직후 history.replace 로 쿼리 제거. 재실행 시 ?openFeed 없어 일찍 return. */
    const history  = useHistory();
    const location = useLocation();
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const feedId = sp.get("openFeed");
        if (!feedId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await channelApi.getFeed(Number(feedId));
                const feed = data?.data ?? data;
                if (!cancelled && feed) {
                    setDeepLinkDetail({ feed, commentId: sp.get("commentId") });
                    /*
                     * 알림으로 들어온 글이 지금 보고 있는 폴더 밖에 있으면, 상세를 닫는 순간
                     * 그 글이 목록에 없어 "글이 사라졌다" 로 보인다. 폴더 선택을 '전체' 로
                     * 되돌려 달라고 부모에 알린다(부모가 없으면 안내만).
                     */
                    const scopedFolderId = folderScope.folderId ?? null;
                    const outOfScope = folderScope.onlyUnfiled
                        ? feed.folderId != null
                        : scopedFolderId != null && feed.folderId !== scopedFolderId;
                    if (outOfScope) onRequestAllFolders?.();
                }
            } catch {
                if (!cancelled) notify("피드를 불러오지 못했습니다.", "warning");
            } finally {
                history.replace(location.pathname);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, channelId]);

    // 무한 스크롤 IntersectionObserver
    useEffect(() => {
        if (!sentinelRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) loadMore(); },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasNext, loadingMore, page, channelId]);

    const handleDelete = async (feedId) => {
        if (!(await confirmDialog({ message: "삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.deleteFeed(feedId),
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: silentRefresh,
        });
    };

    const handleUpdate = (feedId, feedTitle, content) => runApiAction({
        request: () => channelApi.updateFeed(feedId, {
            title: feedTitle?.trim() || null,
            content,
            mentionedEmployeeCodes: extractMentionedEmployeeCodes(content, members),
        }),
        silent: true,
        onSuccess: silentRefresh,
    });

    const handlePin = (feedId) => runApiAction({
        request: () => channelApi.pinFeed(feedId),
        errorMsg: serverMsgOr("고정 처리 실패"),
        onSuccess: silentRefresh,
    });

    const handleToggleLike = async (feedId) => {
        const opt = (f) => f.id === feedId
            ? { ...f, likedByMe: !f.likedByMe, likeCount: Math.max(0, (f.likeCount ?? 0) + (f.likedByMe ? -1 : 1)) }
            : f;
        setFeeds(prev => prev.map(opt));
        setPinnedDetail(p => (p && p.id === feedId ? opt(p) : p));
        setDeepLinkDetail(p => (p?.feed && p.feed.id === feedId ? { ...p, feed: opt(p.feed) } : p));
        try {
            const res = await channelApi.toggleFeedLike(feedId);
            const d = res?.data ?? res;
            const apply = (f) => f.id === feedId ? { ...f, likedByMe: d.liked, likeCount: d.likeCount } : f;
            setFeeds(prev => prev.map(apply));
            setPinnedDetail(p => (p && p.id === feedId ? apply(p) : p));
            setDeepLinkDetail(p => (p?.feed && p.feed.id === feedId ? { ...p, feed: apply(p.feed) } : p));
        } catch (err) {
            notify(getApiErrorMessage(err, "좋아요 처리 실패"), "error");
            silentRefresh();
        }
    };

    if (loading) return <LoadingDots />;

    const safeFeeds = Array.isArray(feeds) ? feeds : [];
    const pinnedFeeds = safeFeeds.filter(f => f.pinned);
    // 서버에서 pinned DESC, createdAt DESC 순으로 오므로 클라이언트 정렬 불필요
    const allFeeds = safeFeeds;

    const openPinnedDetail = (feed) => {
        const latest = feeds.find(f => f.id === feed.id) || feed;
        setPinnedDetail(latest);
    };

    // 선택된 피드 — id 로 재조회해 silent refresh 후에도 최신 객체를 쓴다. POLL 은 댓글이 없어 선택 대상 아님.
    const selectedFeed = allFeeds.find(f => f.id === selectedFeedId && f.feedType !== "POLL") || null;
    // 같은 행 재클릭 시 선택 해제 (토글)
    const handleSelectFeed = (id) => setSelectedFeedId(prev => (prev === id ? null : id));

    return (
        <Stack spacing={1.5} sx={{ maxWidth: 1400, width: "100%", mx: "auto" }}>
            {/* 목록 헤더 툴바 — 건수 · 보기 모드 토글 · 새 글 작성 */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" px={0.5}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.secondary }}>전체</Typography>
                    <Typography sx={{ fontSize: 12, color: C.textMuted }}>{allFeeds.length}건</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Stack direction="row" sx={{
                        border: `1px solid ${C.border}`, borderRadius: 1, overflow: "hidden", bgcolor: C.surface,
                    }}>
                        <Tooltip title="피드형 (전체 내용)">
                            <IconButton size="small"
                                onClick={() => setViewMode("feed")}
                                sx={{
                                    borderRadius: 0, p: 0.6,
                                    bgcolor: viewMode === "feed" ? C.primaryBg : "transparent",
                                    color: viewMode === "feed" ? C.primary : C.textMuted,
                                    "&:hover": { bgcolor: viewMode === "feed" ? C.primaryLight : C.bg },
                                }}
                            >
                                <ViewModuleRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="목록형 (제목만)">
                            <IconButton size="small"
                                onClick={() => { setViewMode("list"); setSelectedFeedId(null); }}
                                sx={{
                                    borderRadius: 0, p: 0.6,
                                    bgcolor: viewMode === "list" ? C.primaryBg : "transparent",
                                    color: viewMode === "list" ? C.primary : C.textMuted,
                                    "&:hover": { bgcolor: viewMode === "list" ? C.primaryLight : C.bg },
                                }}
                            >
                                <ViewListRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <ActionButton variant="add"
                        onClick={() => setComposeOpen(true)}
                        sx={{ fontSize: 12.5 }}
                        label="새 글 작성" />
                </Stack>
            </Stack>

            {/* 게시물 작성 모달 — 별도 컴포넌트로 분리.
                onPosted: 자체 피드 목록 silent refresh + 채널 전체(참여자 수·요약 등) 도 silent reload. */}
            <FeedComposeModal
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                channelId={channelId}
                myInfo={myInfo}
                members={members}
                folders={folders}
                defaultFolderId={composeFolderId}
                onPosted={() => { silentRefresh(); onChannelRefresh?.(); }}
            />

            {/* 상단 고정 목록 */}
            {pinnedFeeds.length > 0 && (
                <PinnedList pinnedFeeds={pinnedFeeds} myId={myId} onOpenDetail={openPinnedDetail} onPin={handlePin} />
            )}

            {/* 피드 목록 */}
            {allFeeds.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
                    <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.4 }} />
                    <Typography sx={{ fontSize: 14 }}>아직 게시물이 없습니다.</Typography>
                </Box>
            ) : viewMode === "list" ? (
                // 목록형 — 게시판처럼 한 줄. 클릭 시 FeedDetailModal 오픈.
                /* 목록형 — 게시판 목록과 같은 톤: 카드 테두리 하나 + 행은 hover 배경으로만 구분(가로선 없음). */
                <Paper elevation={0} sx={{ border: `1px solid ${C.borderLight}`, borderRadius: 1.5, overflow: "hidden", bgcolor: C.surface }}>
                    {allFeeds.map(feed => (
                        <FeedListItem
                            key={feed.id} feed={feed} myId={myId}
                            channelId={channelId} members={members}
                            onDelete={handleDelete} onUpdate={handleUpdate}
                            onPin={handlePin} onRefresh={silentRefresh}
                            onToggleLike={handleToggleLike}
                        />
                    ))}
                </Paper>
            ) : (
                /* 피드형 — 글 하나가 카드 하나.
                   예전엔 한 컨테이너 안에서 행마다 가로선을 그었는데(디스코드식), 글이 10건이면
                   회색 줄도 10개가 생겨 피드가 표처럼 보였다. 이제 카드 사이 <b>여백</b>이 구분을 맡고
                   선은 카드 테두리 하나뿐이다(사내 톤 = 그림자 대신 옅은 테두리). */
                <Stack spacing={1.25}>
                    {allFeeds.map(feed =>
                        feed.feedType === "POLL" ? (
                            <Box key={feed.id} sx={FEED_CARD_SX}>
                                <PollFeedItem
                                    feed={feed} myId={myId}
                                    onDelete={handleDelete} onPin={handlePin} onRefresh={silentRefresh}
                                    onToggleLike={handleToggleLike}
                                />
                            </Box>
                        ) : (
                            <Box key={feed.id} sx={FEED_CARD_SX}>
                                <FeedItem
                                    feed={feed} myId={myId}
                                    channelId={channelId} members={members}
                                    onDelete={handleDelete} onUpdate={handleUpdate}
                                    onPin={handlePin} onRefresh={silentRefresh}
                                    selected={selectedFeedId === feed.id}
                                    onSelect={handleSelectFeed}
                                    onToggleLike={handleToggleLike}
                                    folders={folders} onFolderChanged={onFolderChanged}
                                />
                            </Box>
                        )
                    )}
                </Stack>
            )}

            {/* 무한 스크롤 sentinel + 로딩 인디케이터 */}
            <Box ref={sentinelRef} sx={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {loadingMore && <LoadingDots compact label="더 불러오고 있어요" sx={{ py: 0 }} />}
                {!loadingMore && !hasNext && allFeeds.length > 0 && (
                    <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>모든 게시물을 불러왔습니다.</Typography>
                )}
            </Box>

            {/* 고정 피드 상세 모달 (TEXT 타입만) */}
            {pinnedDetail && pinnedDetail.feedType !== "POLL" && (
                <FeedDetailModal
                    feed={pinnedDetail} myId={myId} channelId={channelId} members={members}
                    open={Boolean(pinnedDetail)} onClose={() => setPinnedDetail(null)}
                    onRefresh={() => { silentRefresh(); setPinnedDetail(null); }}
                    onUpdate={handleUpdate} onDelete={handleDelete} onToggleLike={handleToggleLike}
                />
            )}

            {/* 알람/deep link 자동 오픈 피드 상세 모달.
                평소 클릭(FeedItem 내부)과 동일한 FeedDetailModal 을 사용해 모달 UI 일관성 보장.
                commentId 가 있으면 CommentItem 의 highlight 가 직접 해당 댓글로 스크롤하므로
                scrollToComments 는 넘기지 않는다 (둘이 동시에 동작하면 스크롤 충돌). */}
            {deepLinkDetail?.feed && deepLinkDetail.feed.feedType !== "POLL" && (
                <FeedDetailModal
                    feed={deepLinkDetail.feed} myId={myId} channelId={channelId} members={members}
                    open={Boolean(deepLinkDetail)}
                    highlightCommentId={deepLinkDetail.commentId}
                    onClose={() => setDeepLinkDetail(null)}
                    onRefresh={() => { silentRefresh(); setDeepLinkDetail(null); }}
                    onUpdate={handleUpdate} onDelete={handleDelete} onToggleLike={handleToggleLike}
                />
            )}

            {/* ── 하단 댓글 독 — 행 선택 시 스크롤 영역 하단에 sticky 로 떠오른다 (최상위 z-index).
                   행마다 댓글 입력 섹션을 두지 않는 대신, 여기서만 댓글을 작성한다.
                   X · Esc · 같은 행 재클릭으로 닫힘. ── */}
            {selectedFeed && (
                <Box sx={{ position: "sticky", bottom: 8, zIndex: 1201 }}>
                    <Paper elevation={0} sx={{
                        border: `1px solid ${C.primaryBorder}`, borderRadius: 1.5, bgcolor: C.surface,
                        boxShadow: "0 12px 32px rgba(15,23,42,0.18)", overflow: "hidden",
                    }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 2, pt: 1.25 }}>
                            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 14, color: C.primary, flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: 12.5, color: C.textSecondary, flex: 1, minWidth: 0 }}>
                                <Box component="span" sx={{ fontWeight: 700, color: C.textPrimary }}>
                                    {selectedFeed.author?.name}
                                </Box>
                                {"님의 "}
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                    “{(selectedFeed.title || stripHtml(selectedFeed.content) || "게시글").slice(0, 40)}”
                                </Box>
                                {" 에 댓글 작성"}
                            </Typography>
                            {selectedFeed.comments?.length > 0 && (
                                <Typography onClick={() => setDockDetailOpen(true)}
                                    sx={{ fontSize: 12, fontWeight: 600, color: C.primary, cursor: "pointer", flexShrink: 0,
                                        "&:hover": { textDecoration: "underline" } }}>
                                    댓글 {selectedFeed.comments.length}개 보기
                                </Typography>
                            )}
                            <IconButton size="small" onClick={() => setSelectedFeedId(null)} sx={{ p: 0.4, flexShrink: 0 }}>
                                <CloseRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                            </IconButton>
                        </Stack>
                        {/* key — 다른 행 선택 시 입력 중이던 텍스트가 새 피드로 넘어가지 않게 재마운트 */}
                        <CommentInput compact key={selectedFeed.id}
                            feedId={selectedFeed.id} channelId={channelId}
                            members={members} myId={myId} onRefresh={silentRefresh} />
                    </Paper>
                </Box>
            )}

            {/* 독의 "댓글 N개 보기" 상세 모달 */}
            {dockDetailOpen && selectedFeed && (
                <FeedDetailModal
                    feed={selectedFeed} myId={myId} channelId={channelId} members={members}
                    open scrollToComments
                    onClose={() => setDockDetailOpen(false)}
                    onRefresh={silentRefresh}
                    onUpdate={handleUpdate}
                    onDelete={(id) => { handleDelete(id); setDockDetailOpen(false); setSelectedFeedId(null); }}
                    onToggleLike={handleToggleLike}
                />
            )}
        </Stack>
    );
}
